/**
 * useCrypto Group Encryption
 *
 * Sender-Key based group E2EE (encryptForGroup, decryptGroupMessage, rotateSenderKey).
 * distributeSenderKey is an internal helper used by the main store.
 *
 * All functions that need store state accept `get: () => CryptoState` or
 * operate on values passed in — no direct store import to avoid circular deps.
 */

import { toBase64, fromBase64, createSession, type PreKeyBundle } from "@kollegeplus/crypto";
import { api } from "../lib/api";
import {
  saveSenderKey, loadSenderKey, loadPeerSenderKey, loadPeerSenderKeysByUser, savePeerSenderKey,
  cachePlaintext, getCachedPlaintext, noteSeenIter,
  saveChannelRecoveryKey, loadChannelRecoveryKey,
  type StoredChannelRecoveryKey,
} from "../lib/crypto-storage";
import { e2eeLog } from "../lib/e2ee-logger";
import { reportE2eeError } from "../lib/e2ee-telemetry";
import { decideAck, DEAD_ENVELOPE_ATTEMPTS } from "../lib/pending-key-ack";
import { withBusy } from "../lib/busy-guard";
import type { E2EEEnvelope, ServerDeviceKeys } from "@kollegeplus/shared";
import type { CryptoState } from "./useCrypto";

// ─── P4: Epoch Ack Accumulator ───────────────────────────────────────────────
//
// After each successful group-message decrypt the receiver knows which epoch
// (keyId) it can decrypt for a given (senderUserId, senderDeviceId, channelId).
// We accumulate these in a module-level map keyed by "$senderDeviceId:$channelId"
// so rapid decrypts in a burst cost zero network I/O — only the MAX(keyId) per
// slot is kept. The heartbeat in useCrypto.ts drains and reports the map via
// POST /users/me/devices/heartbeat { epochAcks: [...] }.
//
// Invariant: the map stores the HIGHEST acked epoch seen per slot. On drain,
// the ack list is capped at P4_ACK_MAX_PER_HEARTBEAT entries (20) to bound
// per-heartbeat payload. Remaining entries stay in the map and drain on the
// next heartbeat — they self-heal within one heartbeat interval.
// ─────────────────────────────────────────────────────────────────────────────

export interface P4EpochAck {
  senderUserId: string;
  senderDeviceId: string;
  channelId: string;
  ackedEpoch: number;
}

const P4_ACK_MAX_PER_HEARTBEAT = 20;

/** Module-level pending epoch acks. Key: "$senderDeviceId:$channelId" */
const _p4PendingAcks = new Map<string, P4EpochAck>();

/** Called after every successful group-message decrypt to record the acked epoch. */
function _p4RecordAck(
  senderUserId: string,
  senderDeviceId: string,
  channelId: string,
  ackedEpoch: number,
): void {
  const slotKey = `${senderDeviceId}:${channelId}`;
  const existing = _p4PendingAcks.get(slotKey);
  // Only keep the highest acked epoch so a burst of messages doesn't grow the map.
  if (!existing || ackedEpoch > existing.ackedEpoch) {
    _p4PendingAcks.set(slotKey, { senderUserId, senderDeviceId, channelId, ackedEpoch });
  }
}

/**
 * Drain up to P4_ACK_MAX_PER_HEARTBEAT pending epoch acks and return them.
 * Drained entries are removed from the map; entries beyond the cap remain and
 * will be included in the next heartbeat.
 */
export function p4DrainEpochAcks(): P4EpochAck[] {
  const entries = Array.from(_p4PendingAcks.values());
  const batch = entries.slice(0, P4_ACK_MAX_PER_HEARTBEAT);
  for (const ack of batch) {
    _p4PendingAcks.delete(`${ack.senderDeviceId}:${ack.channelId}`);
  }
  return batch;
}

// ─── Task 2: Sender-Key Delivery-Ack Queue ──────────────────────────────────
//
// After successfully importing an inbound sender-key envelope the receiver
// confirms receipt to the server so it can flip the matching `sender_key_acks`
// row to acked=true (and the Task-3 retransmit loop stops chasing this device).
// We accumulate acks in a module-level map keyed by
// "$senderDeviceId:$channelId:$keyId" so a burst of imports costs zero network
// I/O — the heartbeat in useCrypto.ts drains and reports the map via
// POST /users/me/devices/heartbeat { deliveryAcks: [...] }. This is the exact
// P4 epochAcks pattern, one extra parallel array.
//
// Unlike epochAcks (which keeps only MAX(epoch) per slot), a deliveryAck is
// per-generation: distinct keyIds are distinct rows server-side, so the key
// includes keyId and we never coalesce across generations.
// ─────────────────────────────────────────────────────────────────────────────

export interface DeliveryAck {
  channelId: string;
  /** The SENDER's device id (== envelope.senderDeviceId) — what the server keyed the pending row on. */
  senderDeviceId: string;
  /** Integer key generation. For bot sender keys this is botAckKeyId(<UUID>). */
  keyId: number;
}

const DELIVERY_ACK_MAX_PER_HEARTBEAT = 20;

/** Module-level pending delivery acks. Key: "$senderDeviceId:$channelId:$keyId" */
const _pendingDeliveryAcks = new Map<string, DeliveryAck>();

/**
 * Derive the INTEGER ack-keyId for a sender-key generation.
 *
 * Human sender keys already use an integer counter — returned unchanged.
 * Bot channel sender keys identify a generation with a UUID string; the
 * `sender_key_acks.key_id` column is INTEGER, so we fold the UUID to a stable
 * non-negative 31-bit int via FNV-1a.
 *
 * MUST stay byte-for-byte identical to the SERVER copy in
 * `packages/api/src/services/sender-key-ack.service.ts` (botAckKeyId): the
 * server records the pending row with this int (it has the UUID at distribute
 * time) and the client acks with the SAME int (the inbound bot envelope carries
 * the UUID). Do not change without updating both copies + migrating rows.
 */
export function botAckKeyId(keyId: number | string): number {
  if (typeof keyId === "number") return keyId;
  let hash = 0x811c9dc5; // FNV-1a 32-bit offset basis
  for (let i = 0; i < keyId.length; i++) {
    hash ^= keyId.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0; // FNV prime, keep unsigned 32-bit
  }
  return hash & 0x7fffffff; // mask to 31 bits → fits Postgres INTEGER, always ≥ 0
}

/** Queue a delivery ack after a successful sender-key import. Idempotent per slot. */
export function queueDeliveryAck(ack: DeliveryAck): void {
  const slotKey = `${ack.senderDeviceId}:${ack.channelId}:${ack.keyId}`;
  if (!_pendingDeliveryAcks.has(slotKey)) {
    _pendingDeliveryAcks.set(slotKey, ack);
  }
  // Root cause 2026-06-08: the queue used to drain ONLY on the sparse
  // 1h-interval / tab-refocus heartbeat (_runIdleHeartbeat). A typical session
  // (open app → use in a focused tab < 1h → close) fired neither trigger, so
  // the in-memory queue was lost on tab close before any heartbeat drained it
  // (0 of 36 rows ever acked over 24h). Drive transmission from the IMPORT that
  // produces the ack via a short debounced flush; the heartbeat stays a backstop.
  _scheduleDeliveryAckFlush();
}

/**
 * Drain up to DELIVERY_ACK_MAX_PER_HEARTBEAT pending delivery acks and return
 * them. Drained entries are removed; entries beyond the cap remain and go out
 * on the next flush/heartbeat (self-heal within one interval).
 */
export function drainDeliveryAcks(): DeliveryAck[] {
  const entries = Array.from(_pendingDeliveryAcks.values());
  const batch = entries.slice(0, DELIVERY_ACK_MAX_PER_HEARTBEAT);
  for (const ack of batch) {
    _pendingDeliveryAcks.delete(`${ack.senderDeviceId}:${ack.channelId}:${ack.keyId}`);
  }
  return batch;
}

// ── Import-driven delivery-ack flush ─────────────────────────────────────────
// The ack is produced by a sender-key import; transmit it shortly after, not on
// the unrelated heartbeat cadence. Debounced so a burst of imports coalesces.
const DELIVERY_ACK_FLUSH_DEBOUNCE_MS = 3_000;
let _deliveryAckFlushTimer: ReturnType<typeof setTimeout> | null = null;

function _requeueDeliveryAcks(acks: DeliveryAck[]): void {
  // Re-insert directly (NOT via queueDeliveryAck) so a failed flush does not
  // immediately reschedule another — the next import or the heartbeat backstop
  // drains them, avoiding a tight retry spin while the server/device is down.
  for (const a of acks) {
    _pendingDeliveryAcks.set(`${a.senderDeviceId}:${a.channelId}:${a.keyId}`, a);
  }
}

function _scheduleDeliveryAckFlush(): void {
  if (_deliveryAckFlushTimer !== null) return; // already scheduled
  if (typeof setTimeout === "undefined") return;
  _deliveryAckFlushTimer = setTimeout(() => {
    _deliveryAckFlushTimer = null;
    void flushDeliveryAcks();
  }, DELIVERY_ACK_FLUSH_DEBOUNCE_MS);
}

/**
 * Drain pending delivery acks and POST them to the heartbeat endpoint so the
 * server flips the matching sender_key_acks rows. Reuses the heartbeat route
 * (server already wires recordAck there); spkKeyId is omitted so the drift
 * check is skipped for an ack-only call. Requeues on missing-deviceId or POST
 * failure so no ack is dropped.
 */
export async function flushDeliveryAcks(): Promise<void> {
  const acks = drainDeliveryAcks();
  if (acks.length === 0) return;
  try {
    const { useCrypto } = await import("./useCrypto");
    const deviceId = useCrypto.getState().deviceId;
    if (!deviceId) {
      _requeueDeliveryAcks(acks); // not initialized yet — retry later
      return;
    }
    await api.post("/users/me/devices/heartbeat", { deviceId, deliveryAcks: acks });
  } catch {
    _requeueDeliveryAcks(acks); // transient failure — keep for the next attempt
  }
}

/** Test hook: clear any pending debounce timer between cases. */
export function _resetDeliveryAckFlushForTests(): void {
  if (_deliveryAckFlushTimer !== null) {
    clearTimeout(_deliveryAckFlushTimer);
    _deliveryAckFlushTimer = null;
  }
}

// ─── Sender Key Ratcheting Helpers ──────────────────────────────────────────

/** SHA-256 forward hash for sender key ratcheting (forward secrecy) */
async function ratchetKey(keyBytes: Uint8Array): Promise<Uint8Array> {
  const hash = await crypto.subtle.digest("SHA-256", keyBytes as unknown as ArrayBuffer);
  return new Uint8Array(hash);
}

/** Max forward-ratchet steps to prevent DoS from forged iteration counters.
 *  Must accommodate history-recovery (peer redistributes from iter=0 seed and
 *  the recovering receiver has to forward-ratchet to every past iteration).
 *  Ratchet is a single SHA-256; 10k steps = a few ms — safe to be generous. */
const MAX_RATCHET_SKIP = 10_000;

/** Sender key max age before forced rotation (7 days) */
const SENDER_KEY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// F23: per-session dedup so encryptForGroup doesn't redistribute the channel
// recovery key on every single send. Re-populated on page reload (cheap and
// idempotent). Cleared implicitly when the page reloads.
const recoveryKeyDistributedThisSession = new Set<string>();

// ─── Encrypt for Group ───────────────────────────────────────────────────────

export async function encryptForGroup(
  channelId: string,
  memberIds: string[],
  plaintext: string,
  get: () => CryptoState,
): Promise<string> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) {
    throw new Error("E2EE not initialized");
  }

  // Get or create our sender key for this channel
  let senderKey = await loadSenderKey(channelId);

  // Periodic rotation: force new key if older than 7 days
  if (senderKey?.createdAt) {
    const age = Date.now() - new Date(senderKey.createdAt).getTime();
    if (age > SENDER_KEY_MAX_AGE_MS) {
      e2eeLog.log("Sender key expired for channel", channelId, "— rotating (age:", Math.round(age / 86400000), "days)");
      await rotateSenderKey(channelId, memberIds, get);
      senderKey = await loadSenderKey(channelId);
    }
  }

  if (!senderKey) {
    // Generate new sender key
    const keyBytes = crypto.getRandomValues(new Uint8Array(32));
    const seedB64 = toBase64(keyBytes);
    senderKey = {
      channelId,
      senderKey: seedB64,
      initialSeed: seedB64, // preserved for history-recovery redistribution
      keyId: 1,
      iteration: 0,
      createdAt: new Date().toISOString(),
      distributedTo: [],
      };
    await saveSenderKey(senderKey);

    // Distribute sender key to all members (incl. our own other devices)
    const reached = await distributeSenderKey(channelId, memberIds, senderKey, get);
    senderKey.distributedTo = reached;
    await saveSenderKey(senderKey);
    // Critical: brand-new sender key but distribution reached zero slots —
    // every recipient will see "[Nachricht konnte nicht entschlüsselt werden]".
    // Caller still encrypts + sends below; the report is the only signal.
    if (reached.length === 0 && memberIds.length > 0) {
      const { useAuth } = await import("./useAuth");
      const senderUserId = useAuth.getState().user?.id;
      reportE2eeError({
        phase: "distribute_partial",
        senderId: senderUserId,
        senderDeviceId: state.deviceId,
        deviceId: state.deviceId,
        error: `fresh_key_zero_reach channelId=${channelId} keyId=${senderKey.keyId} memberCount=${memberIds.length}`,
      });
    }
  } else {
    // H1b: drop any legacy entries from previous userId-only encoding. Anything
    // without ":" was written by pre-H1b code and can't be trusted — force a
    // full re-distribution this send. Idempotent; recipient re-ACKs silently.
    const existing = (senderKey.distributedTo ?? []).filter((slot) => slot.includes(":"));
    const alreadyDistributed = new Set(existing);

    // Always attempt distribute: _distributeSenderKeyImpl fetches the current
    // device list per member and skips slots already in `alreadyDistributed`.
    // This catches THREE failure modes the old userId-level check missed:
    //   1. Peer added a new device (gets new slot that's not in the set)
    //   2. Peer rotated a device (old deviceId gone, new one distributes)
    //   3. First send after legacy-distributedTo drop (set is empty → full
    //      redistribute, idempotent)
    // fromSeed: catch-up recipients are by definition new slots that never
    // received this key; transmit the iter=0 seed so they can decrypt the
    // *full history* of the channel, not just messages from this point on.
    // Without this, a previously-phantom-filtered device would receive the
    // current ratchet state and replay-reject all earlier iterations.
    try {
      const reached = await distributeSenderKey(channelId, memberIds, senderKey, get, {
        alreadyDistributed,
        fromSeed: true,
      });
      if (reached.length > 0) {
        senderKey.distributedTo = [...new Set([...existing, ...reached])];
        await saveSenderKey(senderKey);
      } else if (existing.length !== (senderKey.distributedTo ?? []).length) {
        // No new reach, but we dropped legacy entries — persist the cleanup.
        senderKey.distributedTo = existing;
        await saveSenderKey(senderKey);
      }
      // Critical: stored sender key but neither old nor new distribution reached
      // any slot. Either /users/keys returned nothing for everybody or every
      // per-device send threw. Recipients can't decrypt this iter.
      if (reached.length === 0 && existing.length === 0 && memberIds.length > 0) {
        const { useAuth } = await import("./useAuth");
        const senderUserId = useAuth.getState().user?.id;
        reportE2eeError({
          phase: "distribute_partial",
          senderId: senderUserId,
          senderDeviceId: state.deviceId,
          deviceId: state.deviceId,
          error: `existing_key_zero_reach channelId=${channelId} keyId=${senderKey.keyId} memberCount=${memberIds.length}`,
        });
      }
    } catch (err) {
      e2eeLog.warn("Failed to distribute sender key:", err);
      const { useAuth } = await import("./useAuth");
      const senderUserId = useAuth.getState().user?.id;
      reportE2eeError({
        phase: "distribute_partial",
        senderId: senderUserId,
        senderDeviceId: state.deviceId,
        deviceId: state.deviceId,
        error: `distribute_threw channelId=${channelId} err=${String(err).slice(0, 200)}`,
      });
    }
  }

  // F15e: ensure a channel-shared recovery key exists and is distributed.
  // Fire-and-forget so we never block a send — primary durability is still
  // the per-user message_backups upload; the channel recovery backup is a
  // best-effort fallback that activates when the per-user decrypt fails.
  //
  // F23: distribute is split from create. On every fresh session we attempt
  // distribute at least once (recoveryKeyDistributedThisSession dedup), so a
  // first-create distribute failure self-heals on the next reload. New-member
  // bootstrap is handled separately in useChat.ts (member_added handler).
  // TODO: track distributedTo per recovery-key slot and skip members already
  // covered, like sender-key. Today every reload re-distributes to all members.
  (async () => {
    try {
      let recoveryKey = await loadChannelRecoveryKey(channelId);
      if (!recoveryKey) {
        const keyBytes = crypto.getRandomValues(new Uint8Array(32));
        recoveryKey = {
          channelId,
          key: toBase64(keyBytes),
          keyId: 1,
          createdAt: new Date().toISOString(),
        };
        await saveChannelRecoveryKey(recoveryKey);
      }
      if (!recoveryKeyDistributedThisSession.has(channelId)) {
        recoveryKeyDistributedThisSession.add(channelId);
        await distributeChannelRecoveryKey(channelId, memberIds, recoveryKey, get);
      }
    } catch (err) {
      e2eeLog.warn("Failed to ensure channel recovery key for", channelId, err);
    }
  })();

  // Encrypt with sender key (AES-256-GCM)
  const keyBytes = fromBase64(senderKey.senderKey);
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const nonce = crypto.getRandomValues(new Uint8Array(12));

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes.buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as ArrayBuffer },
    cryptoKey,
    plaintextBytes as unknown as ArrayBuffer,
  );

  senderKey.iteration++;
  // Forward secrecy: ratchet the sender key after each message
  const ratcheted = await ratchetKey(fromBase64(senderKey.senderKey));
  senderKey.senderKey = toBase64(ratcheted);
  await saveSenderKey(senderKey);

  const envelope: E2EEEnvelope = {
    v: 1,
    type: "group_message",
    senderDeviceId: state.deviceId,
    senderKeyId: senderKey.keyId,
    payload: toBase64(
      new TextEncoder().encode(
        JSON.stringify({
          ct: toBase64(new Uint8Array(encrypted)),
          nonce: toBase64(nonce),
          iter: senderKey.iteration,
          r: 1, // ratcheted flag — receiver must forward-hash to decrypt
        }),
      ),
    ),
  };

  return JSON.stringify(envelope);
}

// ─── Decrypt Group Message ───────────────────────────────────────────────────

export async function decryptGroupMessage(
  senderId: string,
  channelId: string,
  envelopeStr: string,
  messageId?: string,
): Promise<string | null> {
  // Option A — Plaintext-Cache check at the lowest layer (defense-in-depth).
  // The ratchet advances on every successful decrypt and the stateful-mode
  // replay check rejects re-decrypt of the same iter. So once we've decrypted
  // a group message we can never re-derive its key. The cache short-circuits
  // any subsequent decrypt attempt with the previously-derived plaintext.
  if (messageId) {
    const cached = await getCachedPlaintext(messageId).catch(() => undefined);
    if (cached && cached !== "__NO_BACKUP__") return cached;
  }

  try {
    const envelope = JSON.parse(envelopeStr) as E2EEEnvelope;
    if (envelope.v !== 1 || envelope.type !== "group_message") return null;

    // Multi-device senders: each of the sender's devices has its OWN senderKey.
    // Look up the slot keyed by the specific device that encrypted this envelope
    // so simultaneous activity from Laptop + Phone doesn't overwrite each other.
    let senderDeviceId = envelope.senderDeviceId;
    if (!senderDeviceId) {
      // C5 receiver fallback: envelope pre-dates the senderDeviceId stamping fix
      // (sent by an old client or missed the server stamp). Try to route unambiguously
      // to a known device, or fire a deviceId-free requestSenderKey so self-healing
      // (P0 parking) can engage instead of silently dropping the message.
      const knownKeys = await loadPeerSenderKeysByUser(channelId, senderId);
      if (knownKeys.length === 1) {
        // Exactly one known device — unambiguous fallback.
        senderDeviceId = knownKeys[0].senderDeviceId;
        e2eeLog.warn(
          "C5: group envelope missing senderDeviceId from", senderId,
          "— falling back to single known device", senderDeviceId,
        );
      } else {
        // Zero or multiple known devices — cannot safely pick one.
        // Fire requestSenderKey (no device filter) so ALL of the sender's devices
        // receive a re-distribute request, then return null so the message parks
        // in pendingGroupMessages and retries after sender-key-received fires.
        e2eeLog.warn(
          "C5: group envelope missing senderDeviceId from", senderId,
          knownKeys.length === 0
            ? "— no known keys, requesting"
            : `— ${knownKeys.length} known devices, cannot disambiguate, requesting`,
        );
        reportE2eeError({
          phase: "group_no_senderdeviceid",
          senderId,
          error: `channelId=${channelId} knownDevices=${knownKeys.length}`,
        });
        // Reuses requestSenderKey's existing cooldown + circuit-breaker so this
        // path cannot produce request storms even if many stale messages arrive.
        requestSenderKey(channelId, senderId, { force: true });
        return null;
      }
    }

    const peerSenderKey = await loadPeerSenderKey(channelId, senderId, senderDeviceId);
    if (!peerSenderKey) {
      e2eeLog.warn("No sender key for", senderId, "device", senderDeviceId, "in channel", channelId, "— requesting");
      reportE2eeError({
        phase: "group_no_key",
        senderId,
        senderDeviceId,
        error: `channelId=${channelId}`,
      });
      // Force past the 30s cooldown: an earlier unrelated request (login scan,
      // catch-up decrypt, other channel probe) may have set the map within the
      // last 30s. Missing-peer is a legitimate recovery event — if we respect
      // that stale cooldown, the user stays undecryptable until the unrelated
      // timer expires. Anti-cascade protection lives in requestSenderKey itself
      // (short FORCE_MIN_GAP_MS) so same-tick spam from N catch-up messages is
      // still collapsed to one request.
      // DO NOT skipPending here: for a brand-new device that never consumed any
      // pre-key, the pending envelopes are perfectly valid. Only replay-reject
      // and too-many-skips callers should skipPending (the one-time pre-key is
      // already consumed there, making pending envelopes cryptographically dead).
      requestSenderKey(channelId, senderId, { force: true });
      return null;
    }

    const payloadJson = new TextDecoder().decode(fromBase64(envelope.payload));
    const { ct, nonce, iter, r } = JSON.parse(payloadJson) as {
      ct: string;
      nonce: string;
      iter: number;
      r?: number;
    };

    // keyId mismatch check: if the message was encrypted with an older key
    // (lower keyId) than what we have stored, it was from before a rotation.
    // Forward secrecy means the old key is gone — no recovery possible.
    // If our stored key is older (lower keyId), request the newer one.
    if (envelope.senderKeyId !== undefined && peerSenderKey.keyId !== envelope.senderKeyId) {
      if (peerSenderKey.keyId > envelope.senderKeyId) {
        e2eeLog.warn(
          "Message encrypted with rotated-away keyId", envelope.senderKeyId,
          "but stored keyId is", peerSenderKey.keyId, "from", senderId,
          "— forward secrecy: old key is gone, message unrecoverable",
        );
        reportE2eeError({
          phase: "group_keyid_rotated",
          senderId,
          senderDeviceId,
          error: `channelId=${channelId} envelopeKeyId=${envelope.senderKeyId} storedKeyId=${peerSenderKey.keyId}`,
        });
        return null; // no recovery loop — this is by design
      }
      e2eeLog.warn(
        "Stored keyId", peerSenderKey.keyId, "is older than message keyId",
        envelope.senderKeyId, "from", senderId, "— requesting fresh key",
      );
      requestSenderKey(channelId, senderId, { force: true, skipPending: true });
      return null;
    }

    let keyBytes: Uint8Array;

    if (r === 1) {
      // ─── Path 1: Seed-recovery mode (highest priority) ───
      // Seed-recovery mode: peer transmitted its iter=0 seed so we can derive
      // ANY historical iteration deterministically. Ratchet from seed every
      // time — no stored state, no replay-reject on out-of-order history.
      //
      // Iteration accounting: encryptForGroup uses `senderKey.senderKey` BEFORE
      // it ratchets (and increments to `iter`). So iter=N was encrypted with
      // `ratchet^(N-1)(seed)`. First message ever sent (iter=1) uses the seed
      // itself — zero ratchet steps.
      if (peerSenderKey.seed) {
        if (iter < 1 || iter - 1 > MAX_RATCHET_SKIP) {
          e2eeLog.warn("Invalid iter for seed decrypt:", iter, "from", senderId);
          return null;
        }
        keyBytes = fromBase64(peerSenderKey.seed);
        for (let i = 0; i < iter - 1; i++) {
          keyBytes = await ratchetKey(keyBytes);
        }
      }
      // ─── Path 2: Matrix-style firstKnownIndex (Option D) ───
      // Frozen ratchet root: derive per-message key ephemerally from
      // firstKnownKey by stepping (iter - firstKnownIndex) times. Never mutates
      // the persisted root — re-decrypt after reload always succeeds. Replay
      // protection is application-layer via seenIters.
      else if (
        peerSenderKey.firstKnownKey !== undefined &&
        peerSenderKey.firstKnownIndex !== undefined &&
        iter >= peerSenderKey.firstKnownIndex
      ) {
        if (peerSenderKey.seenIters?.includes(iter)) {
          e2eeLog.warn("Replay rejected (app-layer): iter", iter, "already decrypted from", senderId);
          reportE2eeError({
            phase: "group_replay",
            senderId,
            senderDeviceId,
            error: `app-layer iter=${iter} firstKnown=${peerSenderKey.firstKnownIndex}`,
          });
          return null;
        }
        const stepsToSkip = iter - peerSenderKey.firstKnownIndex;
        if (stepsToSkip > MAX_RATCHET_SKIP) {
          e2eeLog.warn("Too many ratchet steps from firstKnown:", stepsToSkip, "— requesting fresh key");
          reportE2eeError({
            phase: "group_iter_too_far_ahead",
            senderId,
            senderDeviceId,
            error: `firstKnown iter=${iter} firstKnownIndex=${peerSenderKey.firstKnownIndex} stepsToSkip=${stepsToSkip}`,
          });
          requestSenderKey(channelId, senderId, { force: true, skipPending: true });
          return null;
        }
        // Ephemeral ratchet — derive key, no state mutation of firstKnownKey
        keyBytes = fromBase64(peerSenderKey.firstKnownKey);
        for (let i = 0; i < stepsToSkip; i++) {
          keyBytes = await ratchetKey(keyBytes);
        }
      }
      // ─── Path 3: Legacy stateful mode (backward compat) ───
      else {
        // Stateful ratchet mode — validate iteration (replay protection)
        if (iter <= peerSenderKey.iteration) {
          e2eeLog.warn("Replay rejected: iter", iter, "≤ stored", peerSenderKey.iteration, "from", senderId, "— requesting fresh key");
          reportE2eeError({
            phase: "group_replay",
            senderId,
            senderDeviceId,
            error: `channelId=${channelId} envelopeIter=${iter} storedIter=${peerSenderKey.iteration}`,
          });
          // Stored key is ahead of received iter: either true replay, or stored key
          // is stale from a previous session. Force past the cooldown and skip the
          // pending fetch — pending envelopes from the initial distribute are now
          // cryptographically dead (one-time pre-key already consumed), so only a
          // fresh WS-driven handleSenderKeyRequest (with fromSeed:true) can recover.
          requestSenderKey(channelId, senderId, { force: true, skipPending: true });
          return null;
        }

        const stepsToSkip = iter - peerSenderKey.iteration - 1;
        if (stepsToSkip > MAX_RATCHET_SKIP) {
          e2eeLog.warn("Too many ratchet steps:", stepsToSkip, "from", senderId, "— requesting fresh key");
          reportE2eeError({
            phase: "group_iter_too_far_ahead",
            senderId,
            senderDeviceId,
            error: `channelId=${channelId} envelopeIter=${iter} storedIter=${peerSenderKey.iteration} stepsToSkip=${stepsToSkip}`,
          });
          requestSenderKey(channelId, senderId, { force: true, skipPending: true });
          return null;
        }

        // Forward-ratchet to the key used for this message
        keyBytes = fromBase64(peerSenderKey.senderKey);
        for (let i = 0; i < stepsToSkip; i++) {
          keyBytes = await ratchetKey(keyBytes);
        }
      }
    } else {
      // Legacy non-ratcheted message — use key as-is (backward compat)
      keyBytes = fromBase64(peerSenderKey.senderKey);
    }

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      keyBytes.buffer as ArrayBuffer,
      { name: "AES-GCM" },
      false,
      ["decrypt"],
    );

    const nonceBytes = fromBase64(nonce);
    const ctBytes = fromBase64(ct);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonceBytes as unknown as ArrayBuffer },
      cryptoKey,
      ctBytes as unknown as ArrayBuffer,
    );

    if (r === 1 && !peerSenderKey.seed) {
      if (peerSenderKey.firstKnownKey !== undefined) {
        // Matrix-style: only update seenIters, never mutate firstKnownKey/firstKnownIndex.
        // Replay protection lives in the seenIters set; the ratchet root stays frozen
        // so re-decrypt after reload always succeeds.
        noteSeenIter(peerSenderKey, iter);
        await savePeerSenderKey(peerSenderKey);
      } else {
        // Legacy stateful: ratchet senderKey forward and persist (existing behavior).
        const ratcheted = await ratchetKey(keyBytes);
        peerSenderKey.senderKey = toBase64(ratcheted);
        peerSenderKey.iteration = iter;
        await savePeerSenderKey(peerSenderKey);
      }
      // Option E — debounced server-side backup. Fire-and-forget; failures
      // must never throw into the decrypt path. Covers both Matrix and
      // legacy stateful branches so any client persisting sender keys also
      // mirrors them to the server.
      import("../lib/session-backup")
        .then(({ uploadSessionBackup }) => uploadSessionBackup(peerSenderKey))
        .catch(() => {});
    }

    const plaintext = new TextDecoder().decode(decrypted);
    if (messageId) {
      cachePlaintext(messageId, plaintext).catch(() => {});
    }
    // P4: record epoch ack — this receiver can decrypt (senderDevice, channel) at keyId.
    // envelope.senderKeyId is the epoch; senderDeviceId is already in scope.
    if (envelope.senderKeyId !== undefined && senderDeviceId) {
      _p4RecordAck(senderId, senderDeviceId, channelId, envelope.senderKeyId);
    }
    return plaintext;
  } catch (err) {
    // AES-GCM decrypt failure typically means our stored peerSenderKey is out
    // of sync with the sender (stale seed, wrong keyId from a device switch,
    // or overwritten by another of sender's devices whose key we never ratcheted
    // to the observed iter). The stateful-ratchet branches above only trigger
    // recovery on "replay rejected" / "too many skips"; a bad ghash tag lands
    // here silently. Fire the same forced recovery so the sender re-distributes
    // a fresh iter=0 seed via handleSenderKeyRequest.
    e2eeLog.warn("Group decryption failed:", err, "— requesting fresh sender key from", senderId);
    reportE2eeError({
      phase: "group_decrypt_failed",
      senderId,
      error: `channelId=${channelId} err=${String(err).slice(0, 200)}`,
    });
    requestSenderKey(channelId, senderId, { force: true, skipPending: true });
    return null;
  }
}

// ─── Decrypt Bot Channel Sender Key Message (F16d) ──────────────────────────
//
// Bot-channel messages are AES-256-GCM with a per-message key derived from the
// cached bot channel sender key seed via HKDF-SHA-256. The derive info encodes
// the specific iteration so re-ordering is safe and replay protection comes for
// free from AES-GCM's authentication tag.
//
// Wire envelope (type = "bot_channel_sender_key_msg"):
//   { v:1, type, keyId, iter, nonce(b64 12B), ct(b64), tag(b64 16B) }
//
// Cached key lookup: (channelId, botUserId, "bot:<keyId>") — see processSenderKeyPlaintext
// for how the slot is stored using "bot:<keyId>" in the senderDeviceId position to
// keep bot entries in a distinct namespace from user→user sender keys.

export interface BotChannelSenderKeyEnvelope {
  v: 1;
  type: "bot_channel_sender_key_msg";
  keyId: string;
  iter: number;
  nonce: string;   // base64 12 bytes
  ct: string;      // base64 ciphertext (no tag)
  tag: string;     // base64 16-byte auth tag
  // F16h: bot's senderDeviceId (or "bot:<keyId>" fallback). Optional for
  // backward-compat with envelopes shipped before the F16h server fix.
  senderDeviceId?: string;
}

/**
 * Type guard that validates all required fields of a bot channel sender key
 * envelope before passing it to the decrypt path. Malformed envelopes produce
 * a clean null + telemetry instead of silent failures from undefined → base64.
 */
export function isValidBotChannelSenderKeyEnvelope(env: unknown): env is BotChannelSenderKeyEnvelope {
  if (!env || typeof env !== "object") return false;
  const e = env as Record<string, unknown>;
  return (
    e.v === 1 &&
    e.type === "bot_channel_sender_key_msg" &&
    typeof e.keyId === "string" && e.keyId.length > 0 &&
    typeof e.iter === "number" && Number.isFinite(e.iter) && e.iter >= 0 &&
    typeof e.nonce === "string" && e.nonce.length > 0 &&
    typeof e.ct === "string" && e.ct.length > 0 &&
    typeof e.tag === "string" && e.tag.length > 0
  );
}

export async function decryptBotChannelSenderKeyMessage(
  channelId: string,
  botUserId: string,
  env: BotChannelSenderKeyEnvelope,
  messageId?: string,
): Promise<string | null> {
  // Option A — Plaintext-Cache check (same defense-in-depth as decryptGroupMessage)
  if (messageId) {
    const cached = await getCachedPlaintext(messageId).catch(() => undefined);
    if (cached && cached !== "__NO_BACKUP__") return cached;
  }

  // Look up cached bot sender key by (channelId, botUserId, "bot:<keyId>").
  // The slot was stored by processSenderKeyPlaintext with "bot:<keyId>" in the
  // senderDeviceId position: id = `${channelId}:${botUserId}:bot:${env.keyId}`.
  const botDeviceId = `bot:${env.keyId}`;
  const stored = await loadPeerSenderKey(channelId, botUserId, botDeviceId);
  if (!stored) {
    e2eeLog.warn(
      "No bot channel sender key cached for", botUserId,
      "channel", channelId, "keyId", env.keyId,
      "— pending distribution may not have arrived yet",
    );
    reportE2eeError({
      phase: "group_no_key",
      senderId: botUserId,
      senderDeviceId: botDeviceId,
      error: `bot_channel_sender_key missing channelId=${channelId} keyId=${env.keyId}`,
    });
    // F16i: bot's distributedTo set already lists this device (key was sent at
    // some point, then lost from IDB — site-data clear, F17 session-reset
    // cascade, etc). distributeBotSenderKey is idempotent so requestSenderKey's
    // GET /users/keys/pending returns empty, and the WS request_sender_key
    // fallback dies silently (bots have no WS handler for it). Use the bot
    // force-redistribute endpoint instead — it removes this user's devices
    // from distributedTo and re-runs distribute, inserting fresh pending rows
    // the client picks up via the existing pending fetch path.
    void forceBotKeyRedistribution(channelId, botUserId);
    return null;
  }

  try {
    // Derive per-message key: HKDF-SHA-256, ikm = seed, no salt, info = utf8("bot-channel-sender-key:msg:<iter>")
    const ikm = await crypto.subtle.importKey(
      "raw",
      fromBase64(stored.senderKey).buffer as ArrayBuffer,
      { name: "HKDF" },
      false,
      ["deriveBits"],
    );

    const infoStr = `bot-channel-sender-key:msg:${env.iter}`;
    const info = new TextEncoder().encode(infoStr);

    const msgKeyBits = await crypto.subtle.deriveBits(
      {
        name: "HKDF",
        hash: "SHA-256",
        salt: new Uint8Array(0).buffer as ArrayBuffer,
        info: info.buffer as ArrayBuffer,
      },
      ikm,
      256, // 32 bytes
    );

    const msgKey = await crypto.subtle.importKey(
      "raw",
      msgKeyBits,
      { name: "AES-GCM" },
      false,
      ["decrypt"],
    );

    // WebCrypto AES-GCM expects ciphertext with auth tag appended.
    // The server emits them as separate fields — concatenate before decrypt.
    const ctBytes = fromBase64(env.ct);
    const tagBytes = fromBase64(env.tag);
    const ctWithTag = new Uint8Array(ctBytes.length + tagBytes.length);
    ctWithTag.set(ctBytes);
    ctWithTag.set(tagBytes, ctBytes.length);

    const nonceBytes = fromBase64(env.nonce);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonceBytes.buffer as ArrayBuffer, tagLength: 128 },
      msgKey,
      ctWithTag.buffer as ArrayBuffer,
    );

    const plaintext = new TextDecoder().decode(decrypted);
    if (messageId) {
      cachePlaintext(messageId, plaintext).catch(() => {});
    }
    return plaintext;
  } catch (err) {
    e2eeLog.warn(
      "Bot channel sender key message decryption failed:", err,
      "channelId", channelId, "botUserId", botUserId, "keyId", env.keyId, "iter", env.iter,
    );
    reportE2eeError({
      phase: "group_decrypt_failed",
      senderId: botUserId,
      senderDeviceId: botDeviceId,
      error: `bot_channel_sender_key_msg channelId=${channelId} iter=${env.iter} err=${String(err).slice(0, 200)}`,
    });
    return null;
  }
}

// ─── Rotate Sender Key ───────────────────────────────────────────────────────

export async function rotateSenderKey(
  channelId: string,
  memberIds: string[],
  get: () => CryptoState,
): Promise<void> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) return;

  // Generate a fresh sender key
  const keyBytes = crypto.getRandomValues(new Uint8Array(32));
  const seedB64 = toBase64(keyBytes);
  const oldSenderKey = await loadSenderKey(channelId);
  const newKeyId = (oldSenderKey?.keyId ?? 0) + 1;

  const newSenderKey = {
    channelId,
    senderKey: seedB64,
    initialSeed: seedB64, // preserved for history-recovery redistribution
    keyId: newKeyId,
    iteration: 0,
    createdAt: new Date().toISOString(),
    // H1b: start empty. The distributeSenderKey call below returns the
    // (userId:deviceId) slots that actually received the new key; we write
    // those into distributedTo before saving. Fresh-rotate = full redistribute.
    distributedTo: [] as string[],
  };

  // Distribute BEFORE saving — if distribution fails, keep old key
  const reached = await distributeSenderKey(channelId, memberIds, newSenderKey, get);
  newSenderKey.distributedTo = reached;
  await saveSenderKey(newSenderKey);

  e2eeLog.log("Rotated sender key for channel", channelId, "keyId:", newKeyId);
}

// ─── F22c Task 5: wrap-time drift guard ─────────────────────────────────────

/**
 * Compare our own entry in the `/users/keys` response against local keys.
 *
 * Returns `true` (no guard trip) when:
 * - `ownServerDevice` is absent (our device wasn't in the bundle — not a drift signal)
 * - `ownServerDevice.deviceId !== local.deviceId` (shouldn't happen; treat as absent)
 * - server and local SPK pubs match
 *
 * Returns `false` (drift detected) only when our device IS present and its
 * published SPK pub differs from what we have locally — i.e., another tab
 * (or a stale-memory re-register) overwrote the server bundle with different
 * key material. Distributing in this state produces ghash-failing envelopes
 * for every recipient who tries to send us an X3DH envelope.
 */
export function ownBundleMatchesLocal(
  ownServerDevice: { deviceId: string; signedPreKey: { publicKey: string } } | undefined,
  local: { deviceId: string; spkPub: string },
): boolean {
  if (!ownServerDevice || ownServerDevice.deviceId !== local.deviceId) return true;
  return ownServerDevice.signedPreKey.publicKey === local.spkPub;
}

// ─── Distribute Sender Key (internal) ───────────────────────────────────────

/** Public wrapper — guarded by busy-guard so the auto-update/unload machinery
 * can't chop the multi-device distribution in half. Returns the list of
 * (userId:deviceId) slots that received the fresh envelope — callers use this
 * to update `distributedTo` at device granularity (H1b). */
export function distributeSenderKey(
  ...args: Parameters<typeof _distributeSenderKeyImpl>
): Promise<string[]> {
  return withBusy(() => _distributeSenderKeyImpl(...args));
}

async function _distributeSenderKeyImpl(
  channelId: string,
  memberIds: string[],
  senderKey: { senderKey: string; initialSeed?: string; keyId: number; iteration?: number },
  get: () => CryptoState,
  opts: {
    recovery?: boolean;
    fromSeed?: boolean;
    overrideSenderDeviceId?: string;
    /** H1b: "userId:deviceId" slots that already got this key. Skipped during
     * the inner device loop. Devices NOT listed (new device, reinstalled
     * recipient, previously-offline device) still receive an envelope. */
    alreadyDistributed?: Set<string>;
  } = {},
): Promise<string[]> {
  const state = get();
  if (!state.deviceId || !state.deviceKeys) return [];
  // overrideSenderDeviceId: used by proxy-distribution to set the original
  // sender device ID in the envelope instead of our own device ID.
  const envelopeDeviceId = opts.overrideSenderDeviceId ?? state.deviceId;

  // H1b: accumulate the (userId:deviceId) slots we successfully delivered to.
  // Caller merges this into senderKey.distributedTo so future sends can skip
  // already-covered devices but still detect new / lost device slots.
  const reached: string[] = [];

  // fromSeed: recover full history by transmitting the iter=0 seed so the receiver
  // can forward-ratchet to every past iteration. Falls back to current state if seed
  // isn't stored (pre-migration keys).
  const useSeed = opts.fromSeed === true && typeof senderKey.initialSeed === "string";
  const senderKeyPayload = JSON.stringify({
    channelId,
    senderKey: useSeed ? senderKey.initialSeed : senderKey.senderKey,
    keyId: senderKey.keyId,
    iteration: useSeed ? 0 : (senderKey.iteration ?? 0),
    // Recovery marker: set by handleSenderKeyRequest. Receiver passes
    // { force: true } to savePeerSenderKey so a corrupted/ahead stored
    // iteration doesn't block the refresh.
    ...(opts.recovery ? { recovery: true } : {}),
  });

  // Own other devices always get the seed so they can later proxy-distribute
  // on behalf of this device if it goes offline. This is a separate payload
  // with recovery=true + iteration=0 so the receiver stores the seed field.
  const hasSeed = typeof senderKey.initialSeed === "string";
  const ownDevicePayload = hasSeed && !useSeed
    ? JSON.stringify({
        channelId,
        senderKey: senderKey.initialSeed,
        keyId: senderKey.keyId,
        iteration: 0,
        recovery: true,
      })
    : senderKeyPayload; // already has seed if useSeed, or no seed available

  // Always include our own userId in the target list so our OTHER devices
  // (e.g. phone → desktop) also receive the freshly generated sender key.
  // Callers typically pass only the *other* channel members (ChatView filters
  // out currentUserId), which would otherwise leave own other devices unable
  // to decrypt our group messages after a device switch. The loop below
  // skips our current device so we don't distribute to ourselves.
  const { useAuth } = await import("./useAuth");
  const currentUserId = useAuth.getState().user?.id;
  const targetMemberIds = currentUserId
    ? Array.from(new Set([...memberIds, currentUserId]))
    : memberIds;

  // Fetch pre-key bundles for all members at once (one API call)
  let peerKeysResponse: { keys: Record<string, Array<ServerDeviceKeys>> };
  try {
    peerKeysResponse = await api.post("/users/keys", { userIds: targetMemberIds });
  } catch (err) {
    e2eeLog.warn("Failed to fetch pre-key bundles for sender key distribution:", err);
    reportE2eeError({
      phase: "distribute_keys_fetch_failed",
      senderId: currentUserId,
      senderDeviceId: state.deviceId,
      deviceId: state.deviceId,
      error: `channelId=${channelId} memberCount=${targetMemberIds.length} err=${String(err).slice(0, 200)}`,
    });
    return reached;
  }

  // F22c Task 5: wrap-time drift guard.
  // The /users/keys response above already contains our own device entry (we
  // added currentUserId to targetMemberIds above). Compare our published SPK
  // pub against local key material. If they differ, another tab (or a
  // stale-memory re-register) has overwritten the server bundle — wrapping
  // X3DH envelopes now would reference a public key peers will encrypt to but
  // that we cannot decrypt with, producing ghash failures for the entire
  // member list. Abort the distribution (return [] — "reached nobody") and
  // trigger the IDB-truth re-register so the next send attempt works.
  //
  // Throw-vs-return decision: the fresh-key caller (encryptForGroup line ~220)
  // has NO try/catch around distributeSenderKey — a throw would propagate
  // through encryptForGroup and DROP the user's message. The existing-key
  // caller wraps in try/catch and would catch a throw, but returning [] is
  // cleaner (same semantics: reached nobody → distribute_partial telemetry
  // fires, the senderKey.distributedTo stays empty, and the next send
  // re-attempts distribution after the heal). Message is never dropped.
  if (state.deviceKeys && state.deviceId && currentUserId) {
    const localSpkPub = toBase64(state.deviceKeys.signedPreKey.keyPair.publicKey);
    const ownBundles = peerKeysResponse.keys[currentUserId] ?? [];
    const ownServerDevice = ownBundles.find((d) => d.deviceId === state.deviceId);
    if (!ownBundleMatchesLocal(ownServerDevice, { deviceId: state.deviceId, spkPub: localSpkPub })) {
      e2eeLog.warn(
        "[F22c] wrap_drift_guard_tripped: own server bundle SPK differs from local — aborting distribution, triggering self-heal",
        {
          deviceId: state.deviceId,
          serverSpkPub: ownServerDevice?.signedPreKey.publicKey.slice(0, 12),
          localSpkPub: localSpkPub.slice(0, 12),
        },
      );
      reportE2eeError({
        phase: "wrap_drift_guard_tripped",
        deviceId: state.deviceId,
        error: `channelId=${channelId} serverSpk=${(ownServerDevice?.signedPreKey.publicKey ?? "").slice(0, 16)} localSpk=${localSpkPub.slice(0, 16)}`,
      });
      // Fire-and-forget: heal the drift in background. The returned [] means
      // senderKey.distributedTo stays empty; the next send re-distributes
      // after the heal completes (rate-limited to once per 10min).
      void (async () => {
        const { triggerDriftSelfHeal } = await import("./useCrypto");
        await triggerDriftSelfHeal();
      })();
      return reached; // []
    }
  }

  for (const memberId of targetMemberIds) {
    try {
      const memberDevices = peerKeysResponse.keys[memberId];
      if (!memberDevices || memberDevices.length === 0) {
        // Server returned no usable device keys for this member. Most common cause:
        // all of the member's devices are inactive beyond the server cutoff
        // (BOT_DEVICE_INACTIVITY_LIMIT_DAYS = 30d as of F18b Phase 3; was 7d
        // before, "48h" in earlier docs) and were filtered out by
        // user.routes.ts. Less common: member truly has no registered device.
        // Either way we cannot build an X3DH envelope without a pre-key bundle,
        // so no pending_sender_keys row can be created — the recipient will
        // only receive the sender key after they come back online, the sender
        // rotates, and the distribution reruns.
        e2eeLog.warn(
          "Sender-key distribution skipped — no active device keys returned by server",
          { memberId, channelId, reason: "phantom-or-no-devices" },
        );
        // Don't telemetry-spam for distributing to our own user when we are the
        // only device — that's the expected case for a single-device session.
        if (memberId !== currentUserId) {
          reportE2eeError({
            phase: "distribute_no_devices",
            senderId: currentUserId,
            senderDeviceId: state.deviceId,
            deviceId: state.deviceId,
            error: `channelId=${channelId} skippedMemberId=${memberId}`,
          });
        }
        continue;
      }

      // Distribute to ALL devices of this member (multi-device support)
      for (const peerDevice of memberDevices) {
        // Skip our own current device — we already hold the sender key locally.
        if (memberId === currentUserId && peerDevice.deviceId === state.deviceId) continue;
        // H1b: skip devices that are already covered by a previous distribution
        // in this key generation. Recovery / rotate / proxy paths pass no
        // alreadyDistributed set so they always redeliver.
        const slotKey = `${memberId}:${peerDevice.deviceId}`;
        if (opts.alreadyDistributed?.has(slotKey)) continue;
        try {
          const bundle: PreKeyBundle = {
            identityKey: fromBase64(peerDevice.identityKey),
            signingKey: fromBase64(peerDevice.signingKey),
            signedPreKey: {
              keyId: peerDevice.signedPreKey.keyId,
              publicKey: fromBase64(peerDevice.signedPreKey.publicKey),
              signature: fromBase64(peerDevice.signedPreKey.signature),
            },
            preKey: peerDevice.preKey
              ? {
                  keyId: peerDevice.preKey.keyId,
                  publicKey: fromBase64(peerDevice.preKey.publicKey),
                }
              : null,
          };

          // One-shot X3DH session — NOT saved, doesn't affect DM session
          const tempSession = createSession(state.deviceKeys.identityKeyPair, bundle);
          // Own other devices get the seed payload so they can proxy-distribute
          const isOwnOtherDevice = memberId === currentUserId;
          const encrypted = tempSession.encrypt(isOwnOtherDevice ? ownDevicePayload : senderKeyPayload);

          const envelope: E2EEEnvelope = {
            v: 1,
            type: "sender_key_x3dh",
            senderDeviceId: envelopeDeviceId,
            payload: encrypted,
          };

          await api.post("/users/keys/distribute", {
            recipientId: memberId,
            recipientDeviceId: peerDevice.deviceId,
            channelId,
            // Task 2: carry the integer sender-key generation so the server can
            // record a delivery-ack pending row (keyId is otherwise sealed inside
            // the encrypted payload). The receiver acks with this SAME integer.
            keyId: senderKey.keyId,
            envelope: JSON.stringify(envelope),
          });
          reached.push(`${memberId}:${peerDevice.deviceId}`);
        } catch (deviceErr) {
          e2eeLog.warn("Failed to distribute sender key to device of", memberId, deviceErr);
          reportE2eeError({
            phase: "distribute_per_device_fail",
            senderId: currentUserId,
            senderDeviceId: state.deviceId,
            deviceId: state.deviceId,
            error: `channelId=${channelId} memberId=${memberId} peerDeviceId=${peerDevice.deviceId} err=${String(deviceErr).slice(0, 200)}`,
          });
        }
      }
    } catch (err) {
      e2eeLog.warn("Failed to distribute sender key to", memberId, err);
    }
  }

  return reached;
}

// ─── Distribute Channel Recovery Key (F15e) ─────────────────────────────────
//
// Per-channel AES-256 recovery key delivered to every member device through
// the SAME X3DH envelope flow that distributes sender keys. The wire envelope
// stays `sender_key_x3dh` (no server-side opcode change); the discriminator
// lives on the inner plaintext as `{ v:1, type:"channel_recovery_key", payload }`.
// processSenderKeyPlaintext in useCrypto-encrypt.ts routes that to
// saveChannelRecoveryKey when it sees the discriminator.
//
// Returns the list of (userId:deviceId) slots that received an envelope —
// callers can use this for telemetry but it is otherwise advisory.

export async function distributeChannelRecoveryKey(
  channelId: string,
  memberIds: string[],
  key: StoredChannelRecoveryKey,
  get: () => CryptoState,
): Promise<string[]> {
  const state = get();
  if (!state.deviceId || !state.deviceKeys) return [];

  const reached: string[] = [];
  const innerPayload = JSON.stringify({
    v: 1,
    type: "channel_recovery_key",
    payload: {
      channelId,
      key: key.key,
      keyId: key.keyId,
    },
  });

  // Always include our own userId so OUR other devices (e.g. phone) also
  // receive the channel recovery key — without this, switching device
  // would leave us unable to fetch/decrypt the channel-shared backup.
  const { useAuth } = await import("./useAuth");
  const currentUserId = useAuth.getState().user?.id;
  const targetMemberIds = currentUserId
    ? Array.from(new Set([...memberIds, currentUserId]))
    : memberIds;

  let peerKeysResponse: { keys: Record<string, Array<ServerDeviceKeys>> };
  try {
    peerKeysResponse = await api.post("/users/keys", { userIds: targetMemberIds });
  } catch (err) {
    e2eeLog.warn("Failed to fetch pre-key bundles for channel recovery key distribution:", err);
    return reached;
  }

  for (const memberId of targetMemberIds) {
    try {
      const memberDevices = peerKeysResponse.keys[memberId];
      if (!memberDevices || memberDevices.length === 0) continue;

      for (const peerDevice of memberDevices) {
        if (memberId === currentUserId && peerDevice.deviceId === state.deviceId) continue;
        try {
          const bundle: PreKeyBundle = {
            identityKey: fromBase64(peerDevice.identityKey),
            signingKey: fromBase64(peerDevice.signingKey),
            signedPreKey: {
              keyId: peerDevice.signedPreKey.keyId,
              publicKey: fromBase64(peerDevice.signedPreKey.publicKey),
              signature: fromBase64(peerDevice.signedPreKey.signature),
            },
            preKey: peerDevice.preKey
              ? {
                  keyId: peerDevice.preKey.keyId,
                  publicKey: fromBase64(peerDevice.preKey.publicKey),
                }
              : null,
          };

          const tempSession = createSession(state.deviceKeys.identityKeyPair, bundle);
          const encrypted = tempSession.encrypt(innerPayload);

          const envelope: E2EEEnvelope = {
            v: 1,
            type: "sender_key_x3dh",
            senderDeviceId: state.deviceId,
            payload: encrypted,
          };

          await api.post("/users/keys/distribute", {
            recipientId: memberId,
            recipientDeviceId: peerDevice.deviceId,
            channelId,
            envelope: JSON.stringify(envelope),
          });
          reached.push(`${memberId}:${peerDevice.deviceId}`);
        } catch (deviceErr) {
          e2eeLog.warn(
            "Failed to distribute channel recovery key to device of", memberId, deviceErr,
          );
        }
      }
    } catch (err) {
      e2eeLog.warn("Failed to distribute channel recovery key to", memberId, err);
    }
  }

  return reached;
}

// ─── F22c: Peer-Pull channelRecoveryKey ─────────────────────────────────────
//
// When `tryChannelRecoveryDecrypt` finds no local key, it calls this to ask
// online channel members to re-distribute the key via the WS signaling channel.
// In-memory throttle: 1× per channelId per session to prevent decrypt-loop spam.

const recoveryKeyRequestSent = new Set<string>(); // keyed by channelId

/**
 * Request the channelRecoveryKey from online channel members.
 *
 * Fires a `recovery_key_request` WS event; the server fans it out to
 * online members who each call `distributeChannelRecoveryKey` if they hold
 * the key locally. Throttled to 1 call per channelId per browser session.
 */
export async function requestChannelRecoveryKey(channelId: string): Promise<void> {
  if (recoveryKeyRequestSent.has(channelId)) return;
  recoveryKeyRequestSent.add(channelId);

  try {
    const { useCrypto } = await import("./useCrypto");
    const deviceId = useCrypto.getState().deviceId;
    const { ws } = await import("../lib/ws");
    ws.send({ type: "recovery_key_request", channelId, requesterDeviceId: deviceId ?? "" });
    e2eeLog.info("F22c: recovery_key_request sent for channel", channelId, "device", deviceId);
    reportE2eeError({
      phase: "recovery_key_request_sent",
      deviceId,
      error: `channelId=${channelId}`,
    });
  } catch (err) {
    // Remove from throttle set so a session reload can retry
    recoveryKeyRequestSent.delete(channelId);
    e2eeLog.warn("F22c: failed to send recovery_key_request:", err);
  }
}

// ─── Force Bot Key Redistribution (F16i) ────────────────────────────────────

/** Cooldown per (channelId, botUserId) so a burst of failed bot decrypts in
 *  the same render tick does not POST to the redistribute endpoint repeatedly. */
const botRedistributeRequests = new Map<string, number>();
const BOT_REDISTRIBUTE_MIN_GAP_MS = 5_000;

/**
 * F16i: force the server to re-distribute a bot's channel sender key to the
 * caller's devices and immediately pull + process the resulting pending
 * envelope, then dispatch sender-key-received so parked group messages drain.
 *
 * Use this only from the bot decrypt path (no-cached-key branch) — for normal
 * user→user sender-key recovery, requestSenderKey is still the right call.
 */
export async function forceBotKeyRedistribution(channelId: string, botUserId: string): Promise<void> {
  if (!channelId || !botUserId) return;
  const key = `${channelId}:${botUserId}`;
  const last = botRedistributeRequests.get(key) ?? 0;
  if (Date.now() - last < BOT_REDISTRIBUTE_MIN_GAP_MS) return;
  botRedistributeRequests.set(key, Date.now());

  try {
    await api.post("/users/keys/bot-redistribute", { channelId, botUserId });
  } catch (err) {
    e2eeLog.warn("bot-redistribute endpoint failed:", err);
    return;
  }

  try {
    const { keys } = await api.get<{
      keys: Array<{ id: string; senderId: string; envelope: string }>;
    }>(`/users/keys/pending/${channelId}`);
    if (keys.length === 0) {
      e2eeLog.warn("bot-redistribute: no pending envelopes returned for", channelId);
      return;
    }
    const { useCrypto } = await import("./useCrypto");
    const crypto = useCrypto.getState();
    const successfulIds: string[] = [];
    for (const { id, senderId, envelope } of keys) {
      try {
        await crypto.decryptMessage(senderId, envelope);
        successfulIds.push(id);
      } catch (err) {
        e2eeLog.warn("bot-redistribute: failed to process envelope from", senderId, err);
      }
    }
    if (successfulIds.length > 0) {
      try { await api.post(`/users/keys/pending/ack`, { ids: successfulIds }); } catch { /* best-effort */ }
      window.dispatchEvent(new CustomEvent("sender-key-received", { detail: { channelId } }));
    }
  } catch (err) {
    e2eeLog.warn("bot-redistribute: failed to fetch pending after redistribute:", err);
  }
}

// ─── Request Sender Key ─────────────────────────────────────────────────────

/** Rate-limit sender key requests to avoid spam loops */
const senderKeyRequests = new Map<string, number>();
// KOL-82: consecutive rounds where no pending envelope produced a key.
const pendingFailureRounds = new Map<string, number>();
const REQUEST_COOLDOWN_MS = 30_000; // 30s per channel pair (non-force path)
const FORCE_MIN_GAP_MS = 5_000; // 5s floor even for force — prevents 1/s request storms

/** Circuit breaker: track consecutive force-requests per channel:user.
 *  If we keep requesting the same key and decryption keeps failing,
 *  stop hammering. The key is likely unrecoverable (rotated away, device gone). */
const forceRetryCount = new Map<string, number>();
const FORCE_MAX_RETRIES = 3; // after 3 force-requests, lock out for 5min
const FORCE_LOCKOUT_MS = 5 * 60_000;
const forceLockoutUntil = new Map<string, number>();

/**
 * Fetch pending sender keys from the server (persisted during distribution).
 * Falls back to WebSocket request_sender_key if no pending keys found.
 */
export function requestSenderKey(
  channelId: string,
  targetUserId: string,
  opts: { force?: boolean; skipPending?: boolean } = {},
): void {
  // DIAG: catch undefined/falsy channelId from callers — produces /pending/undefined bug
  if (!channelId || !targetUserId) {
    e2eeLog.error(
      "requestSenderKey called with invalid args",
      { channelId, targetUserId },
      new Error("requestSenderKey stack").stack,
    );
    return;
  }

  const key = `${channelId}:${targetUserId}`;

  // Circuit breaker: if locked out, don't request
  const lockout = forceLockoutUntil.get(key);
  if (lockout) {
    if (Date.now() < lockout) {
      return; // still locked out
    }
    forceLockoutUntil.delete(key);
    forceRetryCount.delete(key);
  }

  // `force` bypasses the long 30s cooldown — used by recovery paths.
  // Tracks consecutive force-requests; after FORCE_MAX_RETRIES, locks out.
  const lastRequest = senderKeyRequests.get(key) ?? 0;
  const minGap = opts.force ? FORCE_MIN_GAP_MS : REQUEST_COOLDOWN_MS;
  if (Date.now() - lastRequest < minGap) return;

  if (opts.force) {
    const count = (forceRetryCount.get(key) ?? 0) + 1;
    forceRetryCount.set(key, count);
    if (count > FORCE_MAX_RETRIES) {
      e2eeLog.warn("Circuit breaker: stopping sender key requests for", key, "after", FORCE_MAX_RETRIES, "retries — lockout for 5min");
      forceLockoutUntil.set(key, Date.now() + FORCE_LOCKOUT_MS);
      return;
    }
  }
  senderKeyRequests.set(key, Date.now());

  (async () => {
    // `skipPending` is used by replay-reject / too-many-skips callers where
    // the pending envelope is guaranteed to be useless (the one-time pre-key
    // was consumed on initial WS delivery; re-decrypting fails with AES-GCM
    // ghash error). Go straight to WS so handleSenderKeyRequest fires on the
    // sender and a fresh recovery envelope arrives via push + upsert.
    let pendingSucceeded = false;
    if (!opts.skipPending) {
      try {
        const { keys } = await api.get<{ keys: Array<{ id: string; senderId: string; envelope: string }> }>(
          `/users/keys/pending/${channelId}`,
        );

        if (keys.length > 0) {
          e2eeLog.info("Fetched", keys.length, "pending sender keys from server for channel", channelId);
          const { useCrypto } = await import("./useCrypto");
          const crypto = useCrypto.getState();
          // Track each successfully-processed envelope by id so failed envelopes stay on the server
          // and can be retried. Bulk-channel ACK would burn the failures alongside the successes.
          // KOL-82: decrypting the envelope and STORING the key are two
          // different things. Acking on "decrypt did not throw" deleted the
          // envelope server-side while the key never landed here — the loop
          // then repeated forever (measured 17.08.: 10 redistributes, 5
          // fetches, 12 acks, still no readable bot message). Verify against
          // the key store before acking.
          const { loadPeerSenderKeysForChannel } = await import("../lib/crypto-storage");
          const keyIdsBefore = new Set(
            (await loadPeerSenderKeysForChannel(channelId).catch(() => [])).map((k) => k.id),
          );
          const verifiedIds: string[] = [];
          const unverifiedIds: string[] = [];
          const failedIds: string[] = [];
          for (const { id, senderId, envelope } of keys) {
            let plaintext: string | null = null;
            try {
              plaintext = await crypto.decryptMessage(senderId, envelope);
            } catch (err) {
              e2eeLog.warn("Failed to process pending sender key from", senderId, err);
              failedIds.push(id);
              reportE2eeError({
                phase: "pending_key_decrypt_failed",
                senderId,
                error: `pending sender key threw channelId=${channelId}: ${String(err)}`,
              });
              continue;
            }
            if (plaintext === null) {
              // decryptMessage does NOT throw when there is no session — it
              // returns null. Treating "did not throw" as "decrypted" made the
              // telemetry blame storage for what was really a missing session,
              // and cost hours of diagnosis on 17.08. (the bots were sending
              // ratchet-format keys to a browser that had none).
              const envelopeType = (() => {
                try { return (JSON.parse(envelope) as { type?: string }).type ?? "?"; }
                catch { return "?"; }
              })();
              e2eeLog.warn("Pending sender key could not be decrypted (no session?) from", senderId, "type", envelopeType);
              failedIds.push(id);
              reportE2eeError({
                phase: "pending_key_undecryptable",
                senderId,
                error:
                  `pending sender key not decryptable channelId=${channelId} envelopeType=${envelopeType}` +
                  (envelopeType === "sender_key"
                    ? " — ratchet format but no local session; sender holds a stale session (delete bot session + redistribute)"
                    : ""),
              });
              continue;
            }
            const after = await loadPeerSenderKeysForChannel(channelId).catch(() => []);
            // Verify by CONTENT, not by "a new row appeared": redistributing the
            // same key writes the same row id (channelId:senderId:bot:keyId), so
            // an id-only check reported "not stored" for perfectly good updates
            // and the envelope was never acked — the loop stayed alive. What
            // matters is whether a usable key for this sender is present now.
            const usable = after.some((k) => k.id.includes(senderId));
            const changed = after.some((k) => !keyIdsBefore.has(k.id));
            if (usable || changed) {
              after.forEach((k) => keyIdsBefore.add(k.id));
              e2eeLog.info("Processed pending sender key from", senderId);
              verifiedIds.push(id);
            } else {
              // Genuinely decrypted (plaintext present) yet nothing landed in
              // storage — now an unambiguous signal, since the "no session"
              // case above is reported separately.
              e2eeLog.warn("Pending sender key decrypted but no key stored — keeping for retry", senderId);
              unverifiedIds.push(id);
              reportE2eeError({
                phase: "pending_key_not_stored",
                senderId,
                error: `envelope decrypted but no sender key stored channelId=${channelId}`,
              });
            }
          }
          const failureRound = pendingFailureRounds.get(channelId) ?? 0;
          const { ack: successfulIds, keepForRetry } = decideAck({
            verified: verifiedIds,
            unverified: unverifiedIds,
            failed: failedIds,
            failureRound,
          });
          if (verifiedIds.length > 0) pendingFailureRounds.delete(channelId);
          else pendingFailureRounds.set(channelId, failureRound + 1);
          if (keepForRetry.length > 0) {
            e2eeLog.info("Keeping", keepForRetry.length, "pending sender key(s) on the server for retry");
          }
          if (successfulIds.length > 0) {
            try {
              await api.post(`/users/keys/pending/ack`, { ids: successfulIds });
            } catch (err) {
              e2eeLog.warn("Failed to ACK pending sender keys:", err);
            }
            // Notify chat to re-decrypt messages
            window.dispatchEvent(new CustomEvent("sender-key-received", { detail: { channelId } }));
            pendingSucceeded = verifiedIds.length > 0;
            // Reset circuit breaker — we got a fresh key, worth retrying decrypt
            forceRetryCount.delete(key);
            forceLockoutUntil.delete(key);
          } else {
            // All envelopes failed — typically stale one-time pre-keys already
            // consumed on first WS delivery. These envelopes are cryptographically
            // dead and will never succeed. ACK (delete) them to prevent refetching
            // on every future requestSenderKey cycle.
            e2eeLog.warn("No pending sender key could be stored for channel", channelId,
              `(round ${failureRound + 1}/${DEAD_ENVELOPE_ATTEMPTS}) — falling back to WS`);
          }
        }
      } catch (err) {
        e2eeLog.warn("Failed to fetch pending sender keys:", err);
      }
    }
    if (pendingSucceeded) return;

    // Fallback: request via WebSocket (requires sender to be online)
    import("../lib/ws").then(({ ws }) => {
      ws.send({ type: "request_sender_key", channelId, targetUserId });
      e2eeLog.info("Requested sender key via WS from", targetUserId, "for channel", channelId);
    }).catch(() => {});
  })();
}

/**
 * Handle incoming sender key request: another member needs our sender key
 * for a channel. Re-distribute it to them.
 */
export function handleSenderKeyRequest(
  ...args: Parameters<typeof _handleSenderKeyRequestImpl>
): Promise<void> {
  return withBusy(() => _handleSenderKeyRequestImpl(...args));
}

async function _handleSenderKeyRequestImpl(
  channelId: string,
  requesterId: string,
  get: () => CryptoState,
): Promise<void> {
  // 1. Distribute our OWN sender key (existing behavior)
  const senderKey = await loadSenderKey(channelId);
  if (senderKey) {
    // fromSeed: transmit iter=0 seed so requester can derive all historical iterations.
    // Trade-off: grants history-decrypt capability to any group member who requests it —
    // deliberate break of forward secrecy for group-join / device-migration UX.
    await distributeSenderKey(channelId, [requesterId], senderKey, get, { recovery: true, fromSeed: true });
    e2eeLog.info("Re-distributed own sender key to", requesterId, "for channel", channelId, "(recovery, fromSeed)");
  } else {
    e2eeLog.warn("No own sender key to share for channel", channelId);
  }

  // 2. Proxy-distribute sender keys from our OWN other devices.
  await _proxyDistributeKeys(channelId, requesterId, get, true);
}

/**
 * Proxy-distribute sender keys from a specific user to a requester.
 * When ownOnly=true, only distribute keys from our own other devices.
 * When ownOnly=false, distribute keys from the specified senderId.
 */
async function _proxyDistributeKeys(
  channelId: string,
  requesterId: string,
  get: () => CryptoState,
  ownOnly: boolean,
  senderId?: string,
): Promise<void> {
  const state = get();
  if (!state.deviceId) return;

  const { useAuth } = await import("./useAuth");
  const currentUserId = useAuth.getState().user?.id;
  if (!currentUserId) return;

  const { loadPeerSenderKeysByUser } = await import("../lib/crypto-storage");

  // Determine which user's keys to proxy-distribute
  const targetUserId = ownOnly ? currentUserId : senderId;
  if (!targetUserId) return;

  const peerKeys = await loadPeerSenderKeysByUser(channelId, targetUserId);
  for (const peerKey of peerKeys) {
    // When proxying own devices, skip our current device (covered by direct distribution)
    if (ownOnly && peerKey.senderDeviceId === state.deviceId) continue;
    if (!peerKey.seed) {
      e2eeLog.log("Skipping proxy-distribute for device", peerKey.senderDeviceId, "of", targetUserId, "— no seed stored");
      continue;
    }
    const proxySenderKey = {
      senderKey: peerKey.seed,
      initialSeed: peerKey.seed,
      keyId: peerKey.keyId,
      iteration: 0,
    };
    try {
      await distributeSenderKey(channelId, [requesterId], proxySenderKey, get, {
        recovery: true,
        fromSeed: true,
        overrideSenderDeviceId: peerKey.senderDeviceId,
      });
      e2eeLog.info(
        "Proxy-distributed sender key for device", peerKey.senderDeviceId,
        "of user", targetUserId, "to", requesterId, "for channel", channelId,
      );
    } catch (err) {
      e2eeLog.warn("Failed to proxy-distribute for device", peerKey.senderDeviceId, err);
    }
  }
}

/**
 * Handle proxy_sender_key_request: the server asks us to redistribute an
 * OFFLINE user's sender key to a requester. We can do this if we stored
 * the sender's key with seed (received via recovery distribution).
 */
export function handleProxySenderKeyRequest(
  ...args: Parameters<typeof _handleProxySenderKeyRequestImpl>
): Promise<void> {
  return withBusy(() => _handleProxySenderKeyRequestImpl(...args));
}

async function _handleProxySenderKeyRequestImpl(
  channelId: string,
  senderId: string,
  requesterId: string,
  get: () => CryptoState,
): Promise<void> {
  e2eeLog.info("Proxy sender key request: distribute keys of", senderId, "to", requesterId, "for channel", channelId);
  await _proxyDistributeKeys(channelId, requesterId, get, false, senderId);
}
